import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectLevelControls, selectFieldDescriptions } from "../../../redux/selectors";
import FillSources from "./FillSources";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

const LevelControls = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "levelControls"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectLevelControls);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;
  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        comments: jobDetails.Comments || "",
        levelSensingMethod: jobDetails.LevelSensingMethod || "",
        numberOfSetPoints: jobDetails.NumberOfSetPoints || "",
        sensorLocation: jobDetails.SensorLocation || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  /****************Add dropdown options here*************************/

  const levelSensingMethodOptions =
    options?.filter(
      (option) => option.DropdownType === "Level Sensing Method"
    ).sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="mechanical-responsive-grid level-controls-responsive">
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="levelSensingMethod">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("LevelSensingMethod")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Level Sensing Method</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="levelSensingMethod"
                  value={formData.levelSensingMethod || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(
                      e,
                      "Level Sensing Method",
                      levelSensingMethodOptions,
                      {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "LevelSensingMethod",
                        tableKey,
                      }
                    )
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.levelSensingMethod} // Apply dynamic class
                  onChange={handleChange}
                >
                  <option value="">Select Level Sensing Method</option>
                  {levelSensingMethodOptions.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText}
                    </option>
                  ))}
                  {!isValueInDropdown(
                    formData.levelSensingMethod,
                    levelSensingMethodOptions
                  ) &&
                    formData.levelSensingMethod && (
                      <option
                        value={formData.levelSensingMethod}
                        style={{ color: "red" }}
                      >
                        *{formData.levelSensingMethod}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group controlId="numberOfSetPoints">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("NumberOfSetPoints")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Number Of Set Points</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="numberOfSetPoints"
                  value={formData.numberOfSetPoints || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Number Of Set Points", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "NumberOfSetPoints",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.numberOfSetPoints} // Apply dynamic class
                  onChange={handleChange}
                  placeholder="Enter Number Of Set Points"
                />
              </Form.Group>
            </Col>

            <Col className="mechanical-form-fill-row">
              <Form.Group controlId="sensorLocation">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("SensorLocation")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Sensor Location</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="sensorLocation"
                  value={formData.sensorLocation || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Sensor Location", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "SensorLocation",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.sensorLocation} // Apply dynamic class
                  onChange={handleChange}
                  placeholder="Enter Sensor Location"
                />
              </Form.Group>
            </Col>
          </Row>
          <h4 className="subsection-header mb-3">
            Pond Fill Logic
          </h4>

          <Row className="mb-3 mechanical-form-full-width">
            <FillSources zone={zone} />
          </Row>

          <Row className="mb-3 mechanical-form-full-width">
            <Col>
              <Form.Group controlId="comments">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Comments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comments"
                  value={formData.comments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Comments", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "Comments",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.comments} // Apply dynamic class
                  onChange={handleChange}
                  placeholder="Enter Comments"
                />
              </Form.Group>
            </Col>
          </Row>
          </div>

          <div></div>
        </div>
      )}
    </div>
  );
};

export default LevelControls;
